import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Save, Loader2, RotateCcw } from "lucide-react";
import CustomSelect from "../components/Common/CustomSelect";
import { createUser, updateUser, getUserById } from "../services/userManagementService";
import { validate, isRequired, isValidEmail, isValidPassword } from "../utils/validation";
import { MESSAGES } from "../utils/messages";
import { ROLES } from "../constants/roles";

const INITIAL_FORM = {
  name: "",
  email: "",
  password: "",
  role: ROLES.SUPERADMIN as string,
  active: "true",
};

const initialErrors: Record<string, string> = {};

const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>(initialErrors);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [savedForm, setSavedForm] = useState(INITIAL_FORM);
  const [apiError, setApiError] = useState<string | null>(null);

  // Load existing user data for edit
  useEffect(() => {
    if (!isEdit || !id) return;
    setFetching(true);
    getUserById(Number(id))
      .then((user) => {
        const loaded = {
          name: user.name || "",
          email: user.email || "",
          password: "",
          role: user.role || ROLES.SUPERADMIN,
          active: user.active ? "true" : "false",
        };
        setForm(loaded);
        setSavedForm(loaded);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "Failed to load user details";
        toast.error(msg);
        navigate("/users");
      })
      .finally(() => setFetching(false));
  }, [isEdit, id, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setApiError(null);
  };

  const handleReset = () => {
    setForm(isEdit ? { ...savedForm, password: "" } : INITIAL_FORM);
    setErrors({});
    setApiError(null);
  };

  const validateForm = (): boolean => {
    const schema: Record<string, any> = {
      name: [isRequired("Name")],
      email: [isRequired("Email"), isValidEmail],
    };
    if (!isEdit) {
      schema.password = [isRequired("Password"), isValidPassword];
    }
    const errs = validate(schema, form);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) {
      toast.error("Please fix errors in the form before saving.");
      return;
    }

    setLoading(true);
    try {
      if (isEdit && id) {
        await updateUser(Number(id), {
          name: form.name.trim(),
          email: form.email.trim(),
          role: form.role,
          active: form.active === "true",
        });
        toast.success(MESSAGES.success.userUpdated);
      } else {
        await createUser({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          role: form.role,
          active: form.active === "true",
        });
        toast.success(MESSAGES.success.userCreated);
      }
      navigate("/users");
    } catch (err: any) {
      const serverMessage =
        err?.response?.data?.message || err?.message || `Failed to ${isEdit ? "update" : "create"} user`;
      setApiError(serverMessage);
      toast.error(serverMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500">
        <button onClick={() => navigate("/users")} className="hover:text-gray-900 transition-colors">
          Users
        </button>
        <span className="text-gray-400">/</span>
        <span className="text-gray-900 font-medium">{isEdit ? "Edit" : "Add New"}</span>
      </nav>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-5">
          {apiError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-medium">
              {apiError}
            </div>
          )}

          {/* Account Information */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Account Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Name" name="name" value={form.name} onChange={handleChange} error={errors.name} required placeholder="Enter full name" />
              <FormField label="Email" name="email" value={form.email} onChange={handleChange} error={errors.email} required type="email" placeholder="Enter email address" />
              {!isEdit && (
                <FormField label="Password" name="password" value={form.password} onChange={handleChange} error={errors.password} required type="password" placeholder="Enter password" helperText="Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special character" />
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role <span className="text-red-500 ml-0.5">*</span>
                </label>
                <CustomSelect
                  value={form.role}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, role: val }));
                    setApiError(null);
                  }}
                  options={[
                    { value: ROLES.SUPERADMIN, label: "Super Admin" },
                  ]}
                  error={!!errors.role}
                />
                {errors.role && <p className="text-xs text-red-600 mt-1">{errors.role}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <CustomSelect
                  value={form.active}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, active: val }));
                    setApiError(null);
                  }}
                  options={[
                    { value: "true", label: "Active" },
                    { value: "false", label: "Inactive" },
                  ]}
                  error={!!errors.active}
                />
                {errors.active && <p className="text-xs text-red-600 mt-1">{errors.active}</p>}
              </div>
            </div>
          </div>

          {/* Actions */}
          <hr className="border-gray-100" />
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleReset}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={() => navigate("/users")}
              disabled={loading}
              className="px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-primary-600 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? "Update User" : "Create User"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// --- Helper Component ---

const FormField = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  type = "text",
  placeholder,
  helperText,
  className,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  error?: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  helperText?: string;
  className?: string;
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
        error ? "border-red-300 bg-red-50" : "border-gray-200"
      }`}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    {helperText && !error && <p className="text-xs text-gray-500 mt-1">{helperText}</p>}
  </div>
);

export default UserForm;